import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRealtime } from '../contexts/RealtimeContext';
import { 
  ArrowRight, 
  CheckCircle, 
  Users, 
  Award, 
  BookOpen,
  BarChart3,
  FileText,
  HelpCircle,
  Star,
  Plus,
  X,
  Crown,
  User,
  Briefcase,
  Code,
  Palette,
  Headphones,
  Megaphone,
  Database,
  GraduationCap,
  School,
  Heart,
  Sparkles
} from 'lucide-react';
import { useExam } from '../contexts/ExamContext';
import ApplicationForm from '../components/ApplicationForm';
import SponsorForm from '../components/SponsorForm';
import SEOHead from '../components/SEOHead';
import { generatePageStructuredData, generateFAQStructuredData } from '../utils/seoUtils';
import { getRealStats } from '../utils/realStats';
import { getTeamMembers, saveReview, getReviews } from '../firebase/collections';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { recalculateStats } from '../utils/updateStats';

import { getVideoSettings, initializeSystemSettings } from '../utils/systemSettings';
import WhatsAppButton from '../components/common/WhatsAppButton';


const Home = () => {
  const { currentUser, userProfile, isInitialized } = useRealtime();
  const { selectedExams, availableExams, addExam, removeExam } = useExam();
  const [showExamModal, setShowExamModal] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    name: '',
    exam: '',
    rating: 0,
    review: '',
    feedback: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [realStats, setRealStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewSubmitSuccess, setReviewSubmitSuccess] = useState(false);
  const [joinVideoConfig, setJoinVideoConfig] = useState({ url: '', enabled: false, fetched: false });
  const [showJoinVideo, setShowJoinVideo] = useState(false);
  const [joinVideoLoading, setJoinVideoLoading] = useState(false);
  const [joinVideoError, setJoinVideoError] = useState('');
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [isRecalculatingStats, setIsRecalculatingStats] = useState(false);

  const features = [
    {
      icon: FileText,
      title: 'Mock Tests and Practice Tests',
      description: 'Take full length mock tests for CAT, JEE, NEET, UPSC, SSC, Banking and more. Practice with timed tests, track your progress, and get instant results with detailed explanations.'
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics and Results',
      description: 'View detailed test results with accuracy metrics, time analysis, and performance trends. Identify your strong and weak areas to focus your preparation effectively.'
    },
    {
      icon: HelpCircle,
      title: 'AI Powered Doubt Resolution',
      description: 'Get instant help with your doubts using our AI tutor. Upload questions, images, or PDFs and receive step by step solutions, explanations, and study tips.'
    },
    {
      icon: BookOpen,
      title: 'Online Courses and Study Resources',
      description: 'Access structured courses, study materials, revision notes, and educational resources. Enroll in batches, attend live classes, and learn at your own pace.'
    },
    {
      icon: Users,
      title: 'Study Rooms and Live Classes',
      description: 'Join interactive study rooms with video conferencing. Collaborate with peers, attend live classes, participate in group discussions, and learn together.'
    },
    {
      icon: GraduationCap,
      title: 'Find and Hire Tuition Teachers',
      description: 'Search and connect with qualified tuition teachers. Book demo classes, hire teachers for personalized tutoring, manage batches, and track your learning progress with expert guidance.'
    },
    {
      icon: School,
      title: 'Schools, Colleges and Coaching Centers',
      description: 'Browse and apply to schools, colleges, and coaching centers. Access admissions portal, view institution details, submit applications, and manage your educational journey all in one place.'
    },
    {
      icon: Heart,
      title: 'Extra Curricular Activities Hub',
      description: 'Participate in hobbies, activities, polls, and competitions. Join activity registrations, compete on leaderboards, and engage in a vibrant community beyond academics.'
    },
    {
      icon: Award,
      title: 'Study Tracking and Motivation',
      description: 'Track your daily study streak, set goals, manage daily tasks, and get personalized study counseling. Stay motivated with progress tracking and achievement milestones.'
    }
  ];

  const fetchJoinVideoSettings = async () => {
    try {
      // First, ensure system settings exist
      await initializeSystemSettings();
      
      // Get video settings
      const result = await getVideoSettings();
      
      if (!result.success) {
        setJoinVideoError(result.error || 'Unable to load video settings');
        return { url: '', enabled: false, fetched: true };
      }
      
      const { url, enabled, title, description } = result.data;
      
      if (enabled && !url) {
        setJoinVideoError('Video is enabled but URL is not configured');
        return { url: '', enabled: false, fetched: true };
      }
      
      const config = { 
        url, 
        enabled, 
        title, 
        description, 
        fetched: true 
      };
      
      setJoinVideoConfig(config);
      return config;
    } catch (error) {
      setJoinVideoError('Unable to load video settings: ' + error.message);
      const fallback = { url: '', enabled: false, fetched: true };
      setJoinVideoConfig(fallback);
      return fallback;
    }
  };

  const finishJoinFlow = () => {
    setShowJoinVideo(false);
    navigate('/coaching-signup');
  };

  const handleJoinAsClick = async (event) => {
    event?.preventDefault();
    // Download proposal PDF directly
    fetch('https://firebasestorage.googleapis.com/v0/b/mahstry.firebasestorage.app/o/MAHSTRY%20SCHOOL%20PROPOSAL.pdf?alt=media&token=1e9f3fc4-11ac-493a-b41f-fc4bbf5ac6d8')
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'MAHSTRY_SCHOOL_PROPOSAL.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });
    // Redirect to subdomain
    window.location.href = 'https://services.mahstry.com';
  };

  const handleJoinVideoError = () => {
    finishJoinFlow();
  };

  // Function to manually recalculate stats (for admin use)
  const handleRecalculateStats = async () => {
    if (isRecalculatingStats) return;
    
    setIsRecalculatingStats(true);
    try {
      const result = await recalculateStats();
      if (result.success) {
        // Refresh the stats on the page
        const statsResult = await getRealStats();
        if (statsResult.success) {
          setRealStats(statsResult.stats);
        }
        alert('Stats updated successfully!');
      } else {
        alert('Failed to update stats: ' + result.error);
      }
    } catch (error) {
      alert('Error updating stats: ' + error.message);
    } finally {
      setIsRecalculatingStats(false);
    }
  };








  // Fetch real stats on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const result = await getRealStats();
        if (result.success) {
          setRealStats(result.stats);
        }
      } catch (error) {
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Handle keyboard events for video modal
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (showJoinVideo && event.key === 'Escape') {
        finishJoinFlow();
      }
    };

    if (showJoinVideo) {
      document.addEventListener('keydown', handleKeyPress);
      // Prevent scrolling when video is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [showJoinVideo]);



  // Use real stats if available, otherwise fallback to default
  const stats = realStats ? [
    { number: realStats.students, label: 'Students Preparing for Exams' },
    { number: realStats.mockTests, label: 'Mock Tests & Practice Papers' },
    { number: realStats.successRate, label: 'Exam Success Rate' },
    { number: realStats.support, label: 'Study Support & Doubt Resolution' }
  ] : [
    { number: '1M+', label: 'Students Preparing for Exams' },
    { number: '10K+', label: 'Mock Tests & Practice Papers' },
    { number: '98%', label: 'Exam Success Rate' },
    { number: '24/7', label: 'Study Support & Doubt Resolution' }
  ];

  // Fetch reviews from Firebase
  useEffect(() => {
    const loadReviews = async () => {
      try {
        setReviewsLoading(true);
        const result = await getReviews({ limit: 20 }); // Fetch latest 20 reviews
        if (result.success && result.data) {
          setReviews(result.data);
        } else {
          setReviews([]);
        }
      } catch (error) {
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    loadReviews();
  }, []);

  // Fallback team members (used if Firebase fetch fails)
  const fallbackTeamMembers = [
    {
      name: 'Abhishek Sharma',
      role: 'Founder & CEO',
      position: 'Leadership',
      avatar: 'AS',
      color: 'bg-gradient-to-r from-blue-600 to-purple-600'
    },
    {
      name: 'Mahima Kumari',
      role: 'UI/UX Designer & Graphic Designer',
      position: 'Design',
      avatar: 'MK',
      color: 'bg-gradient-to-r from-green-600 to-teal-600'
    },
    {
      name: 'Mayank Kumawat',
      role: 'Marketing Manager',
      position: 'Marketing',
      avatar: 'MK',
      color: 'bg-gradient-to-r from-orange-600 to-red-600'
    },
    {
      name: 'Hariram Yadav',
      role: 'Database Administrator',
      position: 'Database',
      avatar: 'HY',
      color: 'bg-gradient-to-r from-purple-600 to-pink-600'
    },
    {
      name: 'Vasu Agarwal',
      role: 'Content Manager',
      position: 'Content',
      avatar: 'VA',
      color: 'bg-gradient-to-r from-indigo-600 to-blue-600'
    },
    {
      name: 'Harsh Kumawat',
      role: 'Software Engineer',
      position: 'Development',
      avatar: 'HK',
      color: 'bg-gradient-to-r from-amber-600 to-yellow-600'
    },
    {
      name: 'Richa Shrivastava',
      role: 'Backend Engineer',
      position: 'Development',
      avatar: 'RS',
      color: 'bg-gradient-to-r from-yellow-600 to-orange-600'
    },
    {
      name: 'Samarth Sharma',
      role: 'Content Writer',
      position: 'Content',
      avatar: 'SS',
      color: 'bg-gradient-to-r from-emerald-600 to-green-600'
    },
    {
      name: 'Khushi Sharma',
      role: 'Marketing Executive',
      position: 'Marketing',
      avatar: 'KS',
      color: 'bg-gradient-to-r from-rose-600 to-pink-600'
    },
    {
      name: 'Sandeep Sharma',
      role: 'Business Onboarding',
      position: 'Business',
      avatar: 'SS',
      color: 'bg-gradient-to-r from-violet-600 to-purple-600'
    },
    {
      name: 'Muskan Chaudhary',
      role: 'Social Media Handler',
      position: 'Marketing',
      avatar: 'MC',
      color: 'bg-gradient-to-r from-pink-600 to-rose-600'
    },
    {
      name: 'Tanishka',
      role: 'Content Writer',
      position: 'Content',
      avatar: 'TA',
      color: 'bg-gradient-to-r from-cyan-600 to-blue-600'
    },
    {
      name: 'Akshita Sharma',
      role: 'Institute Onboarding',
      position: 'Business',
      avatar: 'AK',
      color: 'bg-gradient-to-r from-fuchsia-600 to-purple-600'
    }
  ];

  // Fetch team members from Firebase
  useEffect(() => {
    const loadTeamMembers = async () => {
      try {
        setTeamMembersLoading(true);
        const result = await getTeamMembers();
        if (result.success && result.data && result.data.length > 0) {
          // Use Firebase members if available
          setTeamMembers(result.data);
        } else {
          // Fallback to hardcoded members if Firebase is empty or fails
          setTeamMembers(fallbackTeamMembers);
        }
      } catch (error) {
        // Fallback to hardcoded members on error
        setTeamMembers(fallbackTeamMembers);
      } finally {
        setTeamMembersLoading(false);
      }
    };

    loadTeamMembers();
  }, []);

  // Use Firebase members if available, otherwise use fallback
  // This ensures Firebase data always takes precedence when it exists
  const displayTeamMembers = teamMembers.length > 0 ? teamMembers : fallbackTeamMembers;

  const getPositionIcon = (position) => {
    switch (position) {
      case 'Leadership':
        return <Crown className="w-4 h-4" />;
      case 'Development':
        return <Code className="w-4 h-4" />;
      case 'Design':
        return <Palette className="w-4 h-4" />;
      case 'Content':
        return <FileText className="w-4 h-4" />;
      case 'Marketing':
        return <Megaphone className="w-4 h-4" />;
      case 'Database':
        return <Database className="w-4 h-4" />;
      case 'Business':
        return <Briefcase className="w-4 h-4" />;
      case 'Support':
        return <Headphones className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const handleExamSelection = async (examId) => {
    await addExam(examId);
    setShowExamModal(false);
    if (currentUser) {
      navigate('/dashboard');
    }
  };

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    setReviewSubmitSuccess(false);
    
    try {
      // Validate form data
      if (!reviewForm.name.trim() || !reviewForm.exam || !reviewForm.rating || !reviewForm.review.trim()) {
        alert('Please fill in all required fields');
        setSubmittingReview(false);
        return;
      }

      // Save review to Firebase
      const result = await saveReview({
        name: reviewForm.name,
        exam: reviewForm.exam,
        rating: reviewForm.rating,
        review: reviewForm.review,
        feedback: reviewForm.feedback
      });

      if (result.success) {
        // Reset form
        setReviewForm({
          name: '',
          exam: '',
          rating: 0,
          review: '',
          feedback: ''
        });
        
        // Show success message
        setReviewSubmitSuccess(true);
        
        // Wait a moment for Firestore to update, then reload reviews
        setTimeout(async () => {
          const reviewsResult = await getReviews({ limit: 20 });
          if (reviewsResult.success && reviewsResult.data) {
            setReviews(reviewsResult.data);
          }
        }, 1000);
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setReviewSubmitSuccess(false);
        }, 3000);
      } else {
        alert('Failed to submit review. Please try again.');
      }
    } catch (error) {
      alert('An error occurred while submitting your review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleReviewChange = (field, value) => {
    setReviewForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRatingClick = (rating) => {
    setReviewForm(prev => ({
      ...prev,
      rating: rating
    }));
  };

  // SEO Data
  const seoData = {
    title: "MahStry - #1 Online Study Platform | Mock Tests, Courses, Exam Preparation",
    description: "India's leading online study platform for competitive exams. Practice mock tests, join study rooms, access courses, check results & admit cards. Prepare for CAT, JEE, NEET, UPSC, SSC, Banking & more with expert guidance.",
    keywords: "online study, mock tests, competitive exams, study platform, exam preparation, CAT preparation, JEE preparation, NEET preparation, UPSC preparation, SSC preparation, banking exams, admit card, result, study rooms, online courses, test series, practice tests, exam coaching, study materials, previous year papers, sample papers, study groups, collaborative learning, exam tips, study strategies, performance analytics, exam calendar, notification, syllabus, study plan, revision notes, flashcards, doubt resolution, expert guidance, exam coaching online, best study app, top education platform, India's leading study platform",
    canonical: "https://mahstry.com"
  };

  const faqData = [
    {
      question: "What is MahStry?",
      answer: "MahStry is India's leading online study platform for competitive exams. We provide mock tests, study rooms, courses, and comprehensive exam preparation materials for CAT, JEE, NEET, UPSC, SSC, Banking and other competitive exams."
    },
    {
      question: "How can I practice for competitive exams?",
      answer: "You can practice for competitive exams using our mock tests, join study rooms for collaborative learning, access structured courses, and use our study materials including previous year papers, sample papers, and revision notes."
    },
    {
      question: "Can I check my exam results and admit cards?",
      answer: "Yes, MahStry provides access to exam results, admit cards, and exam notifications for various competitive exams. You can stay updated with all exam-related information in one place."
    },
    {
      question: "Which exams does MahStry cover?",
      answer: "MahStry covers all major competitive exams including CAT, JEE, NEET, UPSC, SSC, Banking, Railway, Defense, and many more. We provide comprehensive preparation materials for each exam."
    },
    {
      question: "How effective are MahStry's mock tests?",
      answer: "Our mock tests are designed by experts and follow the exact pattern of real exams. With 98% success rate, our students have consistently performed well in their actual exams."
    }
  ];

  const structuredData = [
    generatePageStructuredData('home', seoData),
    generateFAQStructuredData(faqData)
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEOHead 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        structuredData={structuredData}
      />
      {showJoinVideo && (
        <>
          {/* Video Modal Background */}
          <div className="fixed inset-0 z-50 bg-black animate-fadeIn">
            {/* Skip Button - Fixed in top-right corner - Separate layer */}
            <div 
              className="fixed top-4 right-4 space-y-2 pointer-events-auto bg-black/30 p-3 rounded-2xl backdrop-blur-sm"
              style={{ zIndex: 10000 }}
            >
              <button
                onClick={finishJoinFlow}
                className="block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full border-2 border-white shadow-2xl hover:scale-110 font-bold text-xl transition-all duration-200 skip-button-pulse"
              >
                ✕ SKIP
              </button>
              <p className="text-white text-sm text-center font-medium bg-black/70 px-3 py-1 rounded-full border border-white/30">
                Press ESC
              </p>
            </div>
          
          {/* Error State */}
          {joinVideoError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-6 py-4 rounded-lg text-center max-w-md">
                <h3 className="text-lg font-semibold mb-2">Video Error</h3>
                <p className="text-sm mb-4">{joinVideoError}</p>
                <button
                  onClick={finishJoinFlow}
                  className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded transition-colors"
                >
                  Continue to Onboarding
                </button>
              </div>
            </div>
          )}
          
          {/* Loading State */}
          {!joinVideoConfig.url && !joinVideoError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black">
              <div className="text-center space-y-6">
                <div className="animate-spin w-16 h-16 border-4 border-white/20 border-t-white rounded-full mx-auto"></div>
                <div className="space-y-2">
                  <p className="text-white text-xl font-medium">Loading video...</p>
                  <p className="text-white/70 text-sm">Please wait while we prepare your onboarding experience</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Video - Full Screen */}
          {joinVideoConfig.url && !joinVideoError && (
            <div className="absolute inset-0">
              <video
                ref={videoRef}
                key={joinVideoConfig.url}
                src={joinVideoConfig.url}
                className="w-full h-full bg-black cursor-pointer"
                autoPlay
                playsInline
                preload="metadata"
                muted={false}
                onEnded={finishJoinFlow}
                onError={handleJoinVideoError}
                onLoadStart={() => setJoinVideoError('')}
                onClick={(e) => {
                  // Toggle play/pause on click
                  if (e.target.paused) {
                    e.target.play();
                  } else {
                    e.target.pause();
                  }
                }}
                style={{
                  width: '100vw',
                  height: '100vh',
                  objectFit: 'contain' // Maintain aspect ratio
                }}
                onContextMenu={(e) => e.preventDefault()} // Disable right-click menu
                controlsList="nodownload nofullscreen noremoteplayback" // Disable some controls
                disablePictureInPicture // Disable picture-in-picture
                disableRemotePlayback // Disable casting
              />
            </div>
          )}
          </div>
        </>
      )}
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-primary-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs sm:text-sm">M</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-gradient">MahStry</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <a href="#team" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-sm xl:text-base">
                Our Team
              </a>
              <button
                type="button"
                onClick={handleJoinAsClick}
                disabled={joinVideoLoading}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full font-semibold text-sm xl:text-base hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg disabled:opacity-70 whitespace-nowrap"
              >
                🏫 ERP
              </button>
              {currentUser ? (
                <Link to="/dashboard" className="btn-primary text-sm xl:text-base px-4 xl:px-6 py-2 xl:py-2.5">
                  Dashboard
                </Link>
              ) : (
                <div className="flex items-center space-x-3 xl:space-x-4">
                  <Link to="/login" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-sm xl:text-base">
                    Login
                  </Link>
                  <button onClick={handleGetStarted} className="btn-primary text-sm xl:text-base px-4 xl:px-6 py-2 xl:py-2.5">
                    Get Started
                  </button>
                </div>
              )}
            </div>

            {/* Tablet Navigation */}
            <div className="hidden md:flex lg:hidden items-center space-x-3">
              <a href="#team" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-sm">
                Team
              </a>
              {currentUser ? (
                <Link to="/dashboard" className="btn-primary text-sm px-4 py-2">
                  Dashboard
                </Link>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link to="/login" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-sm">
                    Login
                  </Link>
                  <button onClick={handleGetStarted} className="btn-primary text-sm px-4 py-2">
                    Start
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center space-x-1.5">
              {currentUser ? (
                <Link to="/dashboard" className="btn-primary text-xs px-3 py-2 min-h-[36px]">
                  Dashboard
                </Link>
              ) : (
                <div className="flex items-center space-x-1.5">
                  <button
                    type="button"
                    onClick={handleJoinAsClick}
                    disabled={joinVideoLoading}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1.5 rounded-full font-semibold text-xs hover:from-blue-700 hover:to-purple-700 transition-all shadow-sm disabled:opacity-70"
                  >
                    🏫 ERP
                  </button>
                  <Link to="/login" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-xs px-2 py-1">
                    Login
                  </Link>
                  <button onClick={handleGetStarted} className="btn-primary text-xs px-3 py-2 min-h-[36px]">
                    Start
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Enhanced with Animations */}
      <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16 sm:py-20 md:py-24 lg:py-32 xl:py-40 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-400/30 to-purple-400/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -90, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-pink-400/30 to-yellow-400/30 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full shadow-lg mb-6 border border-blue-200 dark:border-blue-800"
            >
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 notranslate">
                India's Education First Platform
              </span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-gray-900 dark:text-white mb-6 sm:mb-8 leading-tight px-2"
            >
              Empowering an
              <motion.span 
                className="text-gradient block mt-2 sm:mt-3"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{
                  backgroundSize: "200% 200%"
                }}
              >
                Educated World
              </motion.span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-600 dark:text-gray-400 mb-8 sm:mb-12 max-w-5xl mx-auto leading-relaxed px-2 notranslate font-normal"
            >
              Connecting students, educators, and institutions on one comprehensive learning platform
            </motion.p>

            {/* Feature Pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-wrap justify-center gap-3 mb-10 sm:mb-14 px-4"
            >
              {[
                { icon: "🎯", text: "Mock Tests" },
                { icon: "🤖", text: "AI Doubt Solving" },
                { icon: "👨‍🏫", text: "Expert Teachers" },
                { icon: "📚", text: "Live Classes" },
                { icon: "🏆", text: "Exam Success" },
                { icon: "🏫", text: "ERP" }
              ].map((feature, index) => (
                <motion.div
                  key={feature.text}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-white font-medium text-sm shadow-md hover:shadow-lg transition-all cursor-default"
                >
                  <span className="mr-2">{feature.icon}</span>
                  {feature.text}
                </motion.div>
              ))}
            </motion.div>
            
            {currentUser && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                <Link 
                  to="/dashboard/mock-tests" 
                  className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
                >
                  <span className="relative z-10 flex items-center">
                    Start Mock Test
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300"></div>
                </Link>
              </motion.div>
            )}
          </div>
        </div>

        {/* Floating Elements */}
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-10 hidden lg:block"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl shadow-xl flex items-center justify-center text-2xl transform rotate-12">
            📚
          </div>
        </motion.div>

        <motion.div
          animate={{
            y: [0, 20, 0],
            rotate: [0, -5, 0]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-20 right-10 hidden lg:block"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl shadow-xl flex items-center justify-center text-2xl transform -rotate-12">
            🎓
          </div>
        </motion.div>
      </section>

      {/* Stats Section - Enhanced */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Our <span className="text-gradient">Impact</span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Growing every day with students, teachers, and institutes across India
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-0 group-hover:opacity-100"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent group-hover:border-blue-500/50">
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
                    {statsLoading ? (
                      <div className="animate-pulse bg-gray-300 dark:bg-gray-600 h-12 w-24 mx-auto rounded"></div>
                    ) : (
                      stat.number
                    )}
                  </div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm sm:text-base font-semibold leading-tight">
                    {stat.label}
                  </div>
                  {/* Icon decoration */}
                  <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    {index === 0 && <Users className="w-8 h-8" />}
                    {index === 1 && <FileText className="w-8 h-8" />}
                    {index === 2 && <Award className="w-8 h-8" />}
                    {index === 3 && <HelpCircle className="w-8 h-8" />}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why MahStry is Different - Education First Model - ENHANCED */}
      <section className="py-20 sm:py-24 lg:py-32 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 lg:mb-20"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6"
            >
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                Education First Philosophy
              </span>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-white mb-6">
              Why <span className="text-gradient notranslate">MahStry</span> is Different
            </h2>
            <p className="text-xl sm:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto font-medium">
              We're not just another ed tech platform. We're an <span className="text-blue-600 dark:text-blue-400 font-bold">education first ecosystem</span> built on values, not profits.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {[
              {
                icon: GraduationCap,
                title: "Education First, Always",
                description: "Unlike profit-driven platforms, we prioritize learning outcomes over revenue. Every feature is designed to genuinely help students succeed, not just to maximize engagement metrics.",
                gradient: "from-blue-500 to-blue-600",
                bgGradient: "from-blue-500/10 to-blue-600/10"
              },
              {
                icon: CheckCircle,
                title: "Transparent & Fair",
                description: "No hidden fees, no aggressive upselling, no misleading promises. What you see is what you get. We believe in honest pricing and transparent communication with our community.",
                gradient: "from-green-500 to-green-600",
                bgGradient: "from-green-500/10 to-green-600/10"
              },
              {
                icon: Users,
                title: "Complete Ecosystem",
                description: "One platform for students, teachers, tutors, schools, and coaching institutes. We connect the entire education community, making quality education accessible to everyone.",
                gradient: "from-purple-500 to-purple-600",
                bgGradient: "from-purple-500/10 to-purple-600/10"
              },
              {
                icon: Award,
                title: "Quality Over Quantity",
                description: "We focus on curated, high quality content and expert guidance rather than overwhelming you with thousands of mediocre resources. Every test, course, and material is carefully designed.",
                gradient: "from-orange-500 to-orange-600",
                bgGradient: "from-orange-500/10 to-orange-600/10"
              },
              {
                icon: Heart,
                title: "Student Well-being Matters",
                description: "We care about mental health and balanced learning. No toxic competition, no pressure tactics. We promote healthy study habits, peer support, and holistic development.",
                gradient: "from-pink-500 to-pink-600",
                bgGradient: "from-pink-500/10 to-pink-600/10"
              },
              {
                icon: BookOpen,
                title: "Built by Educators",
                description: "Our team includes experienced teachers, educators, and students who understand real challenges. We're not just tech people building an app, we're educators building a solution.",
                gradient: "from-indigo-500 to-indigo-600",
                bgGradient: "from-indigo-500/10 to-indigo-600/10"
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                {/* Glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.bgGradient} rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500`}></div>
                
                {/* Card */}
                <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 dark:border-gray-700 group-hover:border-transparent h-full">
                  {/* Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-base">
                    {item.description}
                  </p>

                  {/* Decorative corner */}
                  <div className="absolute top-4 right-4 w-20 h-20 opacity-5 group-hover:opacity-10 transition-opacity">
                    <item.icon className="w-full h-full" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA - Enhanced */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-16 lg:mt-20"
          >
            <div className="relative group">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl blur-2xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
              
              {/* Card */}
              <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-10 lg:p-16 shadow-2xl overflow-hidden">
                {/* Animated background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                  }}></div>
                </div>

                <div className="relative z-10 text-center">
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
                    Join the Education Revolution
                  </h3>
                  <p className="text-xl sm:text-2xl text-blue-100 mb-8 max-w-2xl mx-auto font-medium">
                    Be part of a platform that truly cares about your success. No gimmicks, no false promises, just genuine education.
                  </p>
                  {!currentUser && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleGetStarted}
                      className="group relative inline-flex items-center px-10 py-5 bg-white text-blue-600 rounded-full font-bold text-xl shadow-2xl hover:shadow-3xl transition-all duration-300"
                    >
                      <span className="relative z-10 flex items-center">
                        Get Started Free
                        <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" />
                      </span>
                    </motion.button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reviews & Feedback Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              What Our Students Say
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Read reviews from students who have achieved success with MahStry. 
              Share your own experience and help others make informed decisions.
            </p>
          </div>

          {/* Review Form */}
          <div className="bg-gradient-to-r from-primary-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              Share Your Experience
            </h3>
            {reviewSubmitSuccess && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                  <p className="text-green-800 dark:text-green-200 font-medium">
                    Thank you! Your review has been submitted successfully. It may take a moment to appear.
                  </p>
                </div>
              </div>
            )}
            <form onSubmit={handleReviewSubmit} className="max-w-2xl mx-auto" role="form" aria-label="Review and Feedback Form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="review-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Name
                  </label>
                  <input
                    id="review-name"
                    type="text"
                    value={reviewForm.name}
                    onChange={(e) => handleReviewChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                    placeholder="Enter your name"
                    required
                    aria-describedby="name-help"
                  />
                  <div id="name-help" className="sr-only">Please enter your full name for the review</div>
                </div>
                <div>
                  <label htmlFor="review-exam" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Exam You Prepared For
                  </label>
                  <select 
                    id="review-exam"
                    value={reviewForm.exam}
                    onChange={(e) => handleReviewChange('exam', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                    required
                    aria-describedby="exam-help"
                  >
                    <option value="">Select exam</option>
                    <option value="CAT">CAT</option>
                    <option value="JEE">JEE</option>
                    <option value="NEET">NEET</option>
                    <option value="UPSC">UPSC</option>
                    <option value="SSC">SSC</option>
                    <option value="Banking">Banking</option>
                    <option value="Other">Other</option>
                  </select>
                  <div id="exam-help" className="sr-only">Please select the exam you prepared for</div>
                </div>
              </div>
              
              <div className="mb-6">
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rating
                  </legend>
                  <div className="flex space-x-2" role="radiogroup" aria-label="Rating from 1 to 5 stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingClick(star)}
                        className={`text-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 rounded ${
                          star <= reviewForm.rating 
                            ? 'text-yellow-400' 
                            : 'text-gray-300 hover:text-yellow-400'
                        }`}
                        aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                        aria-pressed={star <= reviewForm.rating}
                        role="radio"
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  <div className="sr-only" aria-live="polite">
                    Current rating: {reviewForm.rating} out of 5 stars
                  </div>
                </fieldset>
              </div>

              <div className="mb-6">
                <label htmlFor="review-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Review
                </label>
                <textarea
                  id="review-text"
                  rows={4}
                  value={reviewForm.review}
                  onChange={(e) => handleReviewChange('review', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                  placeholder="Share your experience with MahStry. How did it help you in your exam preparation?"
                  required
                  aria-describedby="review-help"
                ></textarea>
                <div id="review-help" className="sr-only">Please share your detailed experience with our platform</div>
              </div>

              <div className="mb-6">
                <label htmlFor="feedback-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Feedback & Suggestions
                </label>
                <textarea
                  id="feedback-text"
                  rows={3}
                  value={reviewForm.feedback}
                  onChange={(e) => handleReviewChange('feedback', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                  placeholder="Any suggestions to improve our platform? What features would you like to see?"
                  aria-describedby="feedback-help"
                ></textarea>
                <div id="feedback-help" className="sr-only">Optional: Share suggestions to improve our platform</div>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={submittingReview || reviewForm.rating === 0}
                  className="btn-primary text-lg px-8 py-3 inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  aria-describedby="submit-help"
                >
                  <Star className="w-5 h-5 mr-2" aria-hidden="true" />
                  <span aria-live="polite">
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </span>
                </button>
                <div id="submit-help" className="sr-only">
                  {submittingReview ? 'Please wait while we submit your review' : 
                   reviewForm.rating === 0 ? 'Please select a rating before submitting' : 
                   'Click to submit your review and feedback'}
                </div>
              </div>
            </form>
          </div>

          {/* Existing Reviews */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviewsLoading ? (
              <div className="col-span-full text-center py-12">
                <div className="animate-pulse text-gray-400">Loading reviews...</div>
              </div>
            ) : reviews.length > 0 ? (
              reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="card p-6"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                        size={16} 
                      />
                    ))}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 italic">
                    "{review.review}"
                  </p>
                  {review.feedback && (
                    <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
                      <span className="font-medium">Feedback:</span> {review.feedback}
                    </p>
                  )}
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {review.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {review.exam} Student
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Be the First to Share Your Experience!
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  No reviews yet. Submit your review above to help other students.
                </p>
              </div>
            )}
          </div>

          {/* Load More Reviews Button */}
          <div className="text-center mt-8">
            <button className="btn-secondary inline-flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Load More Reviews
            </button>
          </div>
        </div>
      </section>

      {/* Features Section - ENHANCED */}
      <section className="py-20 sm:py-24 lg:py-32 bg-white dark:bg-gray-900 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-400 to-yellow-400 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 lg:mb-20"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full mb-6"
            >
              <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-bold text-purple-900 dark:text-purple-100">
                All-in-One Platform
              </span>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-white mb-6">
              Complete Educational <span className="text-gradient">Ecosystem</span>
            </h2>
            <p className="text-xl sm:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              From exam preparation to admissions, from tutors to institutes, everything you need for academic success in one place
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="group relative"
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                
                {/* Card */}
                <div className="relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:border-transparent h-full flex flex-col">
                  {/* Icon with animated background */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity"></div>
                    <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg">
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-base flex-grow">
                    {feature.description}
                  </p>

                  {/* Hover arrow */}
                  <div className="mt-4 flex items-center text-blue-600 dark:text-blue-400 font-semibold opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all duration-300">
                    <span className="text-sm">Explore</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>

                  {/* Decorative corner */}
                  <div className="absolute top-4 right-4 w-16 h-16 opacity-5 group-hover:opacity-10 transition-opacity">
                    <feature.icon className="w-full h-full" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Team Section */}
      <section id="team" className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              The dedicated team behind MahStry, working together to help you achieve your goals
            </p>
          </div>
          
          {/* Team Members */}
          <div className="mb-12">
            {/* Abhishek Sharma - Center Top */}
            <div className="flex justify-center mb-12">
              {displayTeamMembers.filter(member => member.name === 'Abhishek Sharma').map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="card p-8 text-center hover:shadow-lg transition-shadow duration-300"
                >
                  <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 mx-auto ${member.color}`}>
                    <span className="text-white font-bold text-xl">{member.avatar}</span>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {member.name}
                  </h4>
                  <p className="text-primary-600 dark:text-primary-400 font-medium mb-3 text-lg">
                    {member.role}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Rest of the Team */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {displayTeamMembers.filter(member => member.name !== 'Abhishek Sharma').map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="card p-6 text-center hover:shadow-lg transition-shadow duration-300"
                >
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto ${member.color}`}>
                    <span className="text-white font-bold text-lg">{member.avatar}</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {member.name}
                  </h4>
                  <p className="text-primary-600 dark:text-primary-400 font-medium mb-2">
                    {member.role}
                  </p>
                  <div className="flex items-center justify-center text-gray-500 dark:text-gray-400">
                    {getPositionIcon(member.position)}
                    <span className="text-sm ml-2">{member.position}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="footer" className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <span className="text-xl font-bold">MahStry</span>
              </div>
              <p className="text-gray-400 mb-6">
                The ultimate mock test and learning platform for competitive exam preparation.
              </p>
              <button 
                onClick={() => setShowApplicationForm(true)}
                className="btn-primary inline-flex items-center"
              >
                <Users className="w-4 h-4 mr-2" />
                Join Our Team
              </button>
            </div>
            
            <div className="flex flex-col justify-between">
              <div>
                <h3 className="font-semibold mb-4">Legal</h3>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                  <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                </ul>
              </div>
              
              <div className="mt-8">
                <h3 className="font-semibold mb-4 text-primary-400">Support Education</h3>
                <p className="text-gray-400 mb-4 text-sm">
                  Help us build an educated generation with a growth mindset. 
                  Your sponsorship empowers students to achieve their dreams.
                </p>
                <button 
                  onClick={() => setShowSponsorForm(true)}
                  className="btn-secondary inline-flex items-center"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Be Our Sponsor
                </button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2026 Mahstry Private Limited. All rights reserved.</p>
            {/* Hidden admin button for stats recalculation */}
            {(currentUser?.email?.includes('admin') || currentUser?.email?.includes('mahstry')) && (
              <button
                onClick={handleRecalculateStats}
                disabled={isRecalculatingStats}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                {isRecalculatingStats ? 'Updating Stats...' : 'Recalculate Stats (Admin)'}
              </button>
            )}
          </div>
        </div>
      </footer>

      {/* Exam Selection Modal */}
      {showExamModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowExamModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Select Your Exams
              </h3>
              <button
                onClick={() => setShowExamModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {}
              {availableExams.length === 0 ? (
                <div className="col-span-2 text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">Loading exams...</p>
                </div>
              ) : (
                availableExams.map((exam) => (
                <button
                  key={exam.id}
                  onClick={() => handleExamSelection(exam.id)}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 text-left"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 ${exam.color} rounded-lg flex items-center justify-center text-white text-xl`}>
                      {exam.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {exam.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {exam.fullName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {exam.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Application Form Modal */}
      <ApplicationForm 
        isOpen={showApplicationForm} 
        onClose={() => setShowApplicationForm(false)} 
      />

      {/* Sponsor Form Modal */}
      <SponsorForm 
        isOpen={showSponsorForm} 
        onClose={() => setShowSponsorForm(false)} 
      />

      {/* WhatsApp Contact Button */}
      <WhatsAppButton phoneNumber="7395007539" />
    </div>
  );
};

export default Home;
